import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Navbar } from "@/components/Navbar";
import { WarningBanner } from "@/components/Compliance";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, BookOpen, ListChecks, FileText, HelpCircle, Lightbulb,
  AlertTriangle, ChevronLeft, ChevronRight, Loader2, Award, Sparkles, Menu, X,
} from "lucide-react";

const TYPE_META = {
  reading: { icon: BookOpen, label: "Reading" },
  checklist: { icon: ListChecks, label: "Checklist" },
  worksheet: { icon: FileText, label: "Worksheet" },
  quick_check: { icon: HelpCircle, label: "Quick check" },
  case_study: { icon: Lightbulb, label: "Case study" },
  red_flag: { icon: AlertTriangle, label: "Red flag" },
  resource: { icon: FileText, label: "Resource" },
};

export default function ModulePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [allModules, setAllModules] = useState([]);
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState({ progress: [], quiz_attempts: [] });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [mod, prog, course] = await Promise.all([
      api.get(`/modules/${id}`),
      api.get(`/me/progress`),
      api.get(`/course`),
    ]);
    setData(mod.data);
    setProgress(prog.data);
    setAllModules(course.data.modules);
  };

  useEffect(() => {
    setData(null);
    load().catch(() => navigate("/dashboard"));
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const doneSet = useMemo(() => {
    const s = new Set();
    (progress.progress || []).forEach((p) => {
      if (p.completed) s.add(`${p.item_type}:${p.item_id}`);
    });
    return s;
  }, [progress]);

  const moduleRecord = (progress.progress || []).find((p) => p.item_type === "module" && p.item_id === id && p.completed);
  const quizAttempt = useMemo(() => {
    if (!data?.quiz) return null;
    const attempts = (progress.quiz_attempts || []).filter((a) => a.quiz_id === data.quiz.id);
    return attempts.length ? attempts[0] : null;
  }, [progress, data]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-slate-900" /></div>
      </div>
    );
  }

  const { module: mod, lessons, checklist, quiz, upsells } = data;

  const requiredSkipped = checklist.filter((c) => c.required && !doneSet.has(`checklist:${c.id}`));
  const lessonsDone = lessons.filter((l) => doneSet.has(`lesson:${l.id}`)).length;
  const checklistDone = checklist.filter((c) => doneSet.has(`checklist:${c.id}`)).length;
  const total = lessons.length + checklist.length;
  const pct = total ? Math.round(((lessonsDone + checklistDone) / total) * 100) : 0;

  // Prior required module incomplete warning
  const idx = allModules.findIndex((m) => m.id === id);
  const priorIncomplete = allModules.slice(0, idx).filter((m) => {
    return !(progress.progress || []).some((p) => p.item_type === "module" && p.item_id === m.id && p.completed);
  });

  const warnings = [];
  if (priorIncomplete.length > 0) {
    warnings.push(`You haven't finished ${priorIncomplete.length} earlier module${priorIncomplete.length > 1 ? "s" : ""} (starting with Module ${priorIncomplete[0].module_number}). You can continue, but the modules build on each other.`);
  }
  if (moduleRecord?.skipped_required?.length) {
    warnings.push(`This module is marked complete but ${moduleRecord.skipped_required.length} required step${moduleRecord.skipped_required.length > 1 ? "s were" : " was"} skipped.`);
  }

  const toggleLesson = async (lessonId, current) => {
    setProgress((p) => ({ ...p, progress: optimistic(p.progress, "lesson", lessonId, mod.id, !current) }));
    try {
      await api.post(`/progress/lesson/${lessonId}`, { completed: !current });
    } catch {
      toast.error("Could not save. Try again.");
      load();
    }
  };

  const toggleChecklist = async (itemId, current) => {
    setProgress((p) => ({ ...p, progress: optimistic(p.progress, "checklist", itemId, mod.id, !current) }));
    try {
      await api.post(`/progress/checklist/${itemId}`, { completed: !current });
    } catch {
      toast.error("Could not save. Try again.");
      load();
    }
  };

  const markModuleComplete = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/progress/module/${mod.id}/complete`);
      await load();
      if (res.badges_awarded?.length) {
        res.badges_awarded.forEach((b) => toast.success(`Badge earned: ${b.title}`, { icon: "🏅" }));
      } else {
        toast.success("Module marked complete");
      }
      if (!res.quiz_attempted && quiz) {
        toast.warning("You can continue, but you haven't taken this module's quiz yet.");
      }
    } catch {
      toast.error("Could not mark complete.");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex gap-8">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? "fixed inset-0 z-50 bg-black/40" : "hidden"} lg:block lg:static lg:bg-transparent lg:w-72 shrink-0`} onClick={() => setSidebarOpen(false)}>
          <div className="bg-white h-full lg:h-auto w-72 lg:w-full border-r lg:border lg:rounded-xl border-slate-200 p-4 overflow-y-auto lg:sticky lg:top-20" onClick={(e) => e.stopPropagation()} data-testid="module-sidebar">
            <div className="flex items-center justify-between lg:hidden mb-3">
              <span className="font-semibold text-slate-900">Modules</span>
              <button onClick={() => setSidebarOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-2 hidden lg:block">All modules</p>
            <nav className="space-y-0.5">
              {allModules.map((m) => {
                const complete = (progress.progress || []).some((p) => p.item_type === "module" && p.item_id === m.id && p.completed);
                const active = m.id === id;
                return (
                  <button
                    key={m.id}
                    onClick={() => { setSidebarOpen(false); navigate(`/module/${m.id}`); }}
                    className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors ${active ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"}`}
                    data-testid={`sidebar-module-${m.module_number}`}
                  >
                    {complete ? <CheckCircle2 className={`w-4 h-4 shrink-0 ${active ? "text-emerald-400" : "text-emerald-600"}`} /> : <Circle className="w-4 h-4 shrink-0 opacity-40" />}
                    <span className="truncate"><span className="font-mono text-xs opacity-60 mr-1.5">{String(m.module_number).padStart(2, "0")}</span>{m.title}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 max-w-3xl">
          <button className="lg:hidden mb-4 flex items-center gap-2 text-sm text-slate-600" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar-btn">
            <Menu className="w-4 h-4" /> All modules
          </button>

          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">{mod.stage} · Module {mod.module_number}</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1" data-testid="module-title">{mod.title}</h1>
          <p className="text-slate-600 mt-2 leading-relaxed">{mod.summary}</p>

          <div className="mt-4 flex items-center gap-3">
            <Progress value={moduleRecord ? 100 : pct} className="h-2 flex-1" data-testid="module-progress-bar" />
            <span className="text-sm font-medium text-slate-500">{moduleRecord ? 100 : pct}%</span>
          </div>

          {warnings.length > 0 && <div className="mt-5"><WarningBanner messages={warnings} /></div>}

          {/* Learning objectives + key terms */}
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="font-display font-semibold text-slate-900 mb-2 text-sm">Learning objectives</h3>
              <ul className="space-y-1.5">
                {mod.learning_objectives.map((o, i) => (
                  <li key={i} className="text-sm text-slate-600 flex gap-2"><span className="text-amber-600">→</span>{o}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="font-display font-semibold text-slate-900 mb-2 text-sm">Key terms</h3>
              <div className="flex flex-wrap gap-1.5">
                {mod.key_terms.map((t, i) => <Badge key={i} variant="secondary" className="font-normal">{t}</Badge>)}
              </div>
            </div>
          </div>

          {mod.warning_note && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3" data-testid="module-red-flag">
              <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
              <p className="text-sm text-red-900 leading-relaxed">{mod.warning_note}</p>
            </div>
          )}

          {/* Lessons */}
          <section className="mt-8">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Lessons</h2>
            <div className="space-y-3">
              {lessons.map((l, i) => {
                const meta = TYPE_META[l.lesson_type] || TYPE_META.reading;
                const Icon = meta.icon;
                const done = doneSet.has(`lesson:${l.id}`);
                return (
                  <div key={l.id} className={`rounded-xl border bg-white p-5 transition-colors ${done ? "border-emerald-200" : "border-slate-200"}`} data-testid={`lesson-${l.id}`}>
                    <div className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-medium text-slate-400">{mod.module_number}.{l.lesson_number}</span>
                          <Badge variant="outline" className="text-[10px] font-normal">{meta.label}</Badge>
                          {l.is_required && <Badge className="text-[10px] bg-amber-100 text-amber-900 hover:bg-amber-100 border-0">Required</Badge>}
                          <span className="text-xs text-slate-400">· {l.estimated_minutes} min</span>
                        </div>
                        <h3 className="font-display font-semibold text-slate-900 mt-1">{l.title}</h3>
                        <p className="text-[15px] text-slate-600 mt-2 leading-relaxed whitespace-pre-line">{l.body_content}</p>
                        {l.lesson_type === "worksheet" && <WorksheetField lessonId={l.id} fields={l.worksheet_fields} />}
                        <Button
                          size="sm"
                          variant={done ? "outline" : "default"}
                          className={`mt-3 ${done ? "" : "bg-slate-900 hover:bg-slate-800"}`}
                          onClick={() => toggleLesson(l.id, done)}
                          data-testid={`lesson-complete-${l.id}`}
                        >
                          {done ? <><CheckCircle2 className="w-4 h-4 mr-1.5 text-emerald-600" /> Completed</> : "Mark as read"}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Action step */}
          {mod.action_step && (
            <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="module-action-step">
              <h3 className="font-display font-semibold text-amber-900 flex items-center gap-2 mb-1"><Lightbulb className="w-4 h-4" /> Action step</h3>
              <p className="text-sm text-amber-900 leading-relaxed">{mod.action_step}</p>
            </div>
          )}

          {/* Checklist */}
          {checklist.length > 0 && (
            <section className="mt-8">
              <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Workbook checklist</h2>
              <p className="text-sm text-slate-500 mb-3">Tick each item as you complete it. Required items are flagged.</p>
              <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                {checklist.map((c) => {
                  const done = doneSet.has(`checklist:${c.id}`);
                  return (
                    <label key={c.id} className="flex items-start gap-3 p-4 cursor-pointer hover:bg-slate-50" data-testid={`lesson-checklist-item-${c.id}`}>
                      <Checkbox checked={done} onCheckedChange={() => toggleChecklist(c.id, done)} className="mt-0.5" data-testid={`checklist-checkbox-${c.id}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-sm ${done ? "text-slate-400 line-through" : "text-slate-800"}`}>{c.title}</span>
                          {c.required && <Badge className="text-[10px] bg-red-100 text-red-800 hover:bg-red-100 border-0">Required</Badge>}
                        </div>
                        {c.required && !done && c.warning_if_skipped && (
                          <p className="text-xs text-amber-700 mt-1">{c.warning_if_skipped}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
              {requiredSkipped.length > 0 && (
                <p className="text-xs text-amber-700 mt-2" data-testid="checklist-skip-note">
                  {requiredSkipped.length} required item{requiredSkipped.length > 1 ? "s" : ""} still open. You can continue, but completing them is recommended.
                </p>
              )}
            </section>
          )}

          {/* Quiz */}
          {quiz && (
            <QuizSection quiz={quiz} attempt={quizAttempt} onDone={load} />
          )}

          {/* Module upsell reminder */}
          {upsells?.length > 0 && (
            <div className="mt-8 space-y-3">
              {upsells.map((u) => (
                <div key={u.id} className="rounded-xl border border-amber-200 bg-amber-50/60 p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`module-upsell-${u.id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-amber-600" />
                      <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border-0">{u.price_text}</Badge>
                    </div>
                    <h3 className="font-display font-semibold text-slate-900">{u.title}</h3>
                    <p className="text-sm text-slate-600 mt-0.5">{u.description}</p>
                  </div>
                  <a href={u.cta_url} target="_blank" rel="noreferrer">
                    <Button variant="outline" data-testid={`module-upsell-cta-${u.id}`}>{u.cta_text}</Button>
                  </a>
                </div>
              ))}
            </div>
          )}

          {/* Complete module + nav */}
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 pt-6">
            <div>
              {moduleRecord ? (
                <span className="flex items-center gap-2 text-emerald-700 font-medium" data-testid="module-completed-label"><Award className="w-5 h-5" /> Module completed</span>
              ) : (
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={markModuleComplete} disabled={busy} data-testid="mark-module-complete-button">
                  {busy ? "Saving…" : "Mark module complete"}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {idx > 0 && (
                <Button variant="outline" onClick={() => navigate(`/module/${allModules[idx - 1].id}`)} data-testid="prev-module-btn"><ChevronLeft className="w-4 h-4 mr-1" /> Previous</Button>
              )}
              {idx < allModules.length - 1 && (
                <Button variant="outline" onClick={() => navigate(`/module/${allModules[idx + 1].id}`)} data-testid="next-module-btn">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function optimistic(records, itemType, itemId, moduleId, completed) {
  const found = records.find((r) => r.item_type === itemType && r.item_id === itemId);
  if (found) return records.map((r) => (r === found ? { ...r, completed } : r));
  return [...records, { id: `tmp-${itemId}`, item_type: itemType, item_id: itemId, module_id: moduleId, completed }];
}

function WorksheetField({ lessonId, fields }) {
  const list = fields && fields.length ? fields : ["Your notes"];
  const [values, setValues] = useState({});
  const [status, setStatus] = useState("idle");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/worksheet/${lessonId}`).then((r) => setValues(r.data.fields || {})).catch(() => {}).finally(() => setLoaded(true));
  }, [lessonId]);

  useEffect(() => {
    if (!loaded) return;
    setStatus("saving");
    const t = setTimeout(async () => {
      try {
        await api.post(`/worksheet/${lessonId}`, { fields: values });
        setStatus("saved");
      } catch {
        setStatus("idle");
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid={`worksheet-field-${lessonId}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Fill in your worksheet</span>
        <span className="text-xs text-slate-400">{status === "saving" ? "Saving…" : status === "saved" ? "Saved ✓" : ""}</span>
      </div>
      {list.map((f, i) => (
        <div key={i} className="space-y-1">
          <label className="text-sm font-medium text-slate-700">{f}</label>
          <Textarea
            value={values[f] || ""}
            onChange={(e) => setValues((v) => ({ ...v, [f]: e.target.value }))}
            rows={2}
            placeholder="Your answer…"
            data-testid={`worksheet-input-${lessonId}-${i}`}
          />
        </div>
      ))}
    </div>
  );
}

function QuizSection({ quiz, attempt, onDone }) {
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (Object.keys(answers).length < quiz.questions.length) {
      toast.error("Please answer every question first.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/quizzes/${quiz.id}/submit`, { answers });
      setResult(data);
      onDone();
    } catch {
      toast.error("Could not submit quiz.");
    }
    setSubmitting(false);
  };

  const resultMap = useMemo(() => {
    const m = {};
    (result?.results || []).forEach((r) => (m[r.question_id] = r));
    return m;
  }, [result]);

  return (
    <section className="mt-8" data-testid="quiz-section">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><HelpCircle className="w-5 h-5 text-amber-600" /> {quiz.title}</h2>
            <p className="text-sm text-slate-500 mt-1">{quiz.question_count} questions · submission recommended before completing the module.</p>
          </div>
          {attempt && !open && (
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-0" data-testid="quiz-previous-score">Last score: {attempt.score}%</Badge>
          )}
        </div>

        {!open && (
          <Button className="mt-4 bg-slate-900 hover:bg-slate-800" onClick={() => { setOpen(true); setResult(null); setAnswers({}); }} data-testid="quiz-start-button">
            {attempt ? "Retake quiz" : "Start quiz"}
          </Button>
        )}

        {open && (
          <div className="mt-5 space-y-5" data-testid="quiz-form">
            {quiz.questions.map((q, qi) => {
              const r = resultMap[q.id];
              return (
                <div key={q.id} className="border-t border-slate-100 pt-4 first:border-0 first:pt-0" data-testid={`quiz-question-${qi + 1}`}>
                  <p className="font-medium text-slate-900">{qi + 1}. {q.question_text}</p>
                  <div className="mt-2 space-y-2">
                    {q.options.map((opt, oi) => {
                      const selected = answers[q.id] === opt;
                      let cls = "border-slate-200 hover:border-slate-400";
                      if (result) {
                        if (opt === r?.correct_answer) cls = "border-emerald-400 bg-emerald-50";
                        else if (selected && !r?.is_correct) cls = "border-red-400 bg-red-50";
                        else cls = "border-slate-200 opacity-70";
                      } else if (selected) {
                        cls = "border-slate-900 bg-slate-50";
                      }
                      return (
                        <button
                          key={oi}
                          disabled={!!result}
                          onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt }))}
                          className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ${cls}`}
                          data-testid={`quiz-option-${qi + 1}-${oi + 1}`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {result && r?.explanation && (
                    <p className="text-xs text-slate-500 mt-2 bg-slate-50 rounded-md px-3 py-2">{r.explanation}</p>
                  )}
                </div>
              );
            })}

            {!result ? (
              <Button className="bg-slate-900 hover:bg-slate-800" onClick={submit} disabled={submitting} data-testid="quiz-submit-button">
                {submitting ? "Submitting…" : "Submit answers"}
              </Button>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-center" data-testid="quiz-score-display">
                <p className="font-display text-3xl font-bold text-slate-900">{result.score}%</p>
                <p className="text-sm text-slate-600 mt-1">{result.correct} of {result.total} correct {result.passed ? "· Passed" : "· Keep practising"}</p>
                <p className="text-xs text-slate-400 mt-1">Your score is saved. Passing is not required for full course access.</p>
                <Button variant="outline" className="mt-4" onClick={() => setOpen(false)} data-testid="quiz-close-button">Close</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
